import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from './ui/badge';
import {
  ChevronDown,
  ChevronUp,
  Music,
  Gamepad2,
  Sparkles
} from 'lucide-react';
import {
  hobbies,
  gameCategories,
  musicGenres,
  playlists
} from '../data/personalLifeData';

import ParticleField from './ParticleField';

// ===============================
// Animation
// ===============================
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: 'easeOut' }
  })
};

// ===============================
// Accent System
// ===============================
const colorMap = {
  hobbies: {
    badge: 'border-primary/30 text-primary bg-primary/5',
    accent: 'hsl(var(--primary))',
    glow: 'hsl(var(--primary) / 0.2)'
  },
  gaming: {
    badge: 'border-secondary/30 text-secondary bg-secondary/5',
    accent: 'hsl(var(--secondary))',
    glow: 'hsl(var(--secondary) / 0.15)'
  },
  music: {
    badge: 'border-accent/30 text-accent bg-accent/5',
    accent: 'hsl(var(--accent))',
    glow: 'hsl(var(--accent) / 0.2)'
  }
};

// ===============================
// Playlist Tile
// ===============================
const PlaylistTile = ({ platform, url, accent, text }) => {
  const isLive = url && url !== '#';

  return (
    <motion.a
      href={isLive ? url : undefined}
      target="_blank"
      rel="noopener noreferrer"
      whileHover={isLive ? { y: -4, scale: 1.03 } : {}}
      className={`relative glass glass-hover rounded-xl p-4 flex flex-col gap-2 ${
        !isLive && 'opacity-50 cursor-not-allowed'
      }`}
      style={{ '--glow': accent }}
    >
      {isLive && (
        <div className="absolute top-2 right-3 flex items-center gap-1 text-xs font-mono text-accent">
          <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
          Live
        </div>
      )}

      <div className="flex items-center gap-3">
        <div
          className="w-8 h-8 rounded-md flex items-center justify-center text-sm font-bold"
          style={{ background: accent, color: 'black' }}
        >
          {platform[0]}
        </div>
        <h4 className="font-heading text-sm font-semibold text-foreground">
          {platform}
        </h4>
      </div>

      <p className="text-xs text-muted-foreground">
        {text}
      </p>

      <span className="text-xs font-mono mt-1" style={{ color: accent }}>
        {isLive ? 'Open Playlist →' : 'Coming Soon'}
      </span>
    </motion.a>
  );
};

// ===============================
// Hobby Tile (Visual Upgrade)
// ===============================
const HobbyTile = ({ hobby }) => (
  <motion.div
    whileHover={{ y: -4, scale: 1.02 }}
    className="glass rounded-xl p-4 flex flex-col gap-2"
  >
    <div className="text-2xl">{hobby.emoji}</div>
    <h4 className="font-semibold text-foreground text-sm">
      {hobby.title}
    </h4>
    <p className="text-xs text-muted-foreground">
      {hobby.description}
    </p>
  </motion.div>
);

// ===============================
// Game Tile (Thumbnail Version)
// ===============================
const GameTile = ({ game }) => {
  return (
    <motion.div
      whileHover={{ y: -6 }}
      transition={{ duration: 0.3 }}
      className="relative rounded-2xl overflow-hidden group cursor-none glass glass-hover"
      style={{ '--glow': 'hsl(var(--secondary) / 0.25)' }}
    >
      {/* Thumbnail */}
      <div className="relative h-40 overflow-hidden">
        <img
          src={game.image}
          alt={game.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          style={{ filter: 'brightness(0.65)', objectFit: 'contain' }}
        />

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

        {/* Category Badge */}
        {game.category && (
          <div className="absolute top-3 left-3">
            <span className="text-[10px] font-mono px-2 py-0.5 rounded-md border border-white/20 text-white bg-black/40 backdrop-blur-sm">
              {game.category}
            </span>
          </div>
        )}

        {/* Multiplayer / Mode Indicator */}
        {game.multiplayer && (
          <div className="absolute top-3 right-3 flex items-center gap-1 text-[10px] font-mono text-emerald-400">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            
          </div>
        )}

        {/* Title */}
        <div className="absolute bottom-3 left-4">
          <p className="text-sm font-semibold text-white tracking-wide">
            {game.title}
          </p>
        </div>
      </div>

      {/* Hover Reveal Panel */}
      {/* <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="bg-black/70 backdrop-blur-md px-4 py-2 rounded-lg text-xs font-mono text-white border border-white/10">
          Explore →
        </div>
      </div> */}
    </motion.div>
  );
};

// ===============================
// Personal Card Wrapper
// ===============================
const PersonalCard = ({
  title,
  subtitle,
  icon: Icon,
  type,
  index,
  children
}) => {
  const [expanded, setExpanded] = useState(false);
  const c = colorMap[type];

  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      custom={index}
      viewport={{ once: true }}
      className="glass glass-hover rounded-2xl p-6 flex flex-col"
      style={{ '--glow': c.glow }}
      whileHover={{ y: -4 }}
    >
      <div className="mb-4">
        <Badge className={`${c.badge} text-xs font-mono mb-3`}>
          {title}
        </Badge>

        <div className="flex items-center gap-2 mb-2">
          <Icon size={18} style={{ color: c.accent }} />
          <h3 className="font-heading font-bold text-xl text-foreground">
            {subtitle}
          </h3>
        </div>
      </div>

      <div className="mb-4">{children}</div>

      <button
        onClick={() => setExpanded(!expanded)}
        className="mt-auto text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
      >
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        {expanded ? 'Less' : 'Explore More'}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mt-3 text-xs text-muted-foreground"
          >
            These interests fuel creativity and strategic thinking beyond engineering.
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ===============================
// Main Section
// ===============================
const PersonalLife = () => {
  return (
    <section id="life" className="relative py-24 px-6 md:px-12 lg:px-24 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 30% 50%, hsl(var(--primary) / 0.05) 0%, transparent 60%)' }}
      />
       {/* Grid dots */}
      <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />

      {/* Particles */}
      <div className="absolute inset-0">
        <ParticleField />
      </div>
      <div className="max-w-7xl mx-auto">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-16 "
        >
          <Badge
            variant="outline"
            className="border-primary/30 text-primary bg-primary/5 mb-4 font-mono text-xs uppercase tracking-wider"
          >
            Personal
          </Badge>

          <h2 className="font-heading font-bold text-4xl md:text-5xl">
            Life Beyond
            <span className="gradient-text"> Code</span>
          </h2>

          <p className="text-muted-foreground mt-4 max-w-xl">
            Creativity, competition, rhythm, and curiosity.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* HOBBIES */}
          <PersonalCard
            title="Creative Interests"
            subtitle="Outside Engineering"
            icon={Sparkles}
            type="hobbies"
            index={0}
          >
            <div className="grid grid-cols-2 gap-4">
              {hobbies.map((hobby, i) => (
                <HobbyTile key={i} hobby={hobby} />
              ))}
            </div>
          </PersonalCard>

          {/* GAMING */}
          <PersonalCard
            title="Gaming"
            subtitle="Strategy & Immersion"
            icon={Gamepad2}
            type="gaming"
            index={1}
          >
            <div className="grid grid-cols-2 gap-4">
              {gameCategories.map((game, i) => (
                <GameTile key={i} game={game} />
              ))}
            </div>
          </PersonalCard>

          {/* MUSIC */}
          <PersonalCard
            title="Music"
            subtitle="Sound & Flow"
            icon={Music}
            type="music"
            index={2}
          >
            <div className="grid grid-cols-2 gap-4 mb-6">
              {playlists.map((playlist, idx) => (
                <PlaylistTile
                  key={idx}
                  platform={playlist.platform}
                  url={playlist.url}
                  accent={playlist.accent}
                  text={playlist.text}
                />
              ))}
            </div>

            <div className="flex flex-wrap gap-1.5">
              {musicGenres.map((genre, idx) => (
                <span
                  key={idx}
                  className="font-mono text-xs px-2 py-0.5 rounded-md border border-border/60 text-muted-foreground hover:text-foreground"
                >
                  {genre}
                </span>
              ))}
            </div>
          </PersonalCard>
        </div>
      </div>
    </section>
  );
};

export default PersonalLife;